import { useState } from "react";
import { motion } from "framer-motion";
import { Download } from "lucide-react";
import axios from "axios";
import { useCopy } from "../i18n";

const EASE = [0.16, 1, 0.3, 1];
const WEB3FORMS_KEY = "41595456-1611-4906-a7ee-2c4e032c9e73";

const FIELD =
  "w-full border-b border-[rgba(251,251,249,0.25)] bg-transparent py-3 text-sm text-[#FBFBF9] placeholder-[#FBFBF9]/35 outline-none transition-colors duration-300 focus:border-[#B89762]";
const LABEL = "mb-1 block text-xs font-medium uppercase tracking-[0.2em] text-[#FBFBF9]/60";

export const Contact = () => {
  const copy = useCopy();
  const t = copy.contact;
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", azienda: "", messaggio: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErrorMsg("");

    try {
      const response = await axios.post("https://api.web3forms.com/submit", {
        access_key: WEB3FORMS_KEY,
        name: form.nome,
        email: form.email,
        phone: form.telefono,
        company: form.azienda,
        message: form.messaggio,
        subject: `Nuovo messaggio dal sito da ${form.nome}`,
      });

      if (response.data.success) {
        setStatus("success");
      } else {
        setErrorMsg(t.form.error);
        setStatus("error");
      }
    } catch (err) {
      setErrorMsg(t.form.error);
      setStatus("error");
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="bg-[#1E2A38] text-[#FBFBF9]">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-16 px-6 py-24 lg:grid-cols-2 lg:gap-12 lg:py-32">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-5 flex items-center gap-4 text-xs font-medium uppercase tracking-[0.25em] text-[#B89762]"
          >
            <span className="h-px w-10 bg-[#B89762]" />
            {t.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="font-serif-display text-4xl font-light tracking-tight sm:text-5xl lg:text-6xl"
          >
            {t.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="mt-6 max-w-md text-base leading-relaxed text-[#FBFBF9]/70"
          >
            {t.desc}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
            className="mt-12 space-y-5"
          >
            {t.rows.map((c) => {
              const external = c.href.startsWith("http");
              const extra = external
                ? { target: "_blank", rel: "noopener noreferrer" }
                : c.download
                  ? { download: "" }
                  : {};
              return (
                <a key={c.label} href={c.href} data-testid={c.testid} className="group flex items-baseline gap-6" {...extra}>
                  <span className="w-20 shrink-0 text-xs uppercase tracking-[0.2em] text-[#FBFBF9]/50">{c.label}</span>
                  <span className="inline-flex items-center gap-1.5 border-b border-transparent pb-0.5 text-[#FBFBF9] transition-all duration-300 group-hover:border-[#B89762] group-hover:text-[#B89762]">
                    {c.value}
                    {c.download && <Download className="h-3.5 w-3.5" />}
                  </span>
                </a>
              );
            })}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          data-testid="contact-form-card"
          className="rounded-3xl border border-[rgba(251,251,249,0.12)] bg-[rgba(251,251,249,0.04)] p-8 backdrop-blur-sm sm:p-10"
        >
          {status === "success" ? (
            <div data-testid="contact-success" className="flex h-full min-h-[420px] flex-col items-center justify-center text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#B89762]/15">
                <svg className="h-6 w-6 text-[#B89762]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <h3 className="mt-6 font-serif-display text-3xl italic">
                {t.form.successTitle.replace("{name}", form.nome.trim().split(" ")[0] || "")}
              </h3>
              <p className="mt-3 max-w-xs text-sm leading-relaxed text-[#FBFBF9]/70">{t.form.successDesc}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="contact-form" noValidate={false}>
              <div className="space-y-7">
                <div>
                  <label htmlFor="nome" className={LABEL}>{t.form.nome} {t.form.required}</label>
                  <input id="nome" data-testid="input-nome" className={FIELD} type="text" required minLength={2} maxLength={100} placeholder={t.form.nomePh} value={form.nome} onChange={update("nome")} />
                </div>
                <div>
                  <label htmlFor="email" className={LABEL}>{t.form.email} {t.form.required}</label>
                  <input id="email" data-testid="input-email" className={FIELD} type="email" required placeholder={t.form.emailPh} value={form.email} onChange={update("email")} />
                </div>
                <div>
                  <label htmlFor="telefono" className={LABEL}>{t.form.telefono} {t.form.required}</label>
                  <input id="telefono" data-testid="input-telefono" className={FIELD} type="tel" required minLength={6} maxLength={30} placeholder={t.form.telefonoPh} value={form.telefono} onChange={update("telefono")} />
                </div>
                <div>
                  <label htmlFor="azienda" className={LABEL}>{t.form.azienda} {t.form.required}</label>
                  <input id="azienda" data-testid="input-azienda" className={FIELD} type="text" required minLength={2} maxLength={120} placeholder={t.form.aziendaPh} value={form.azienda} onChange={update("azienda")} />
                </div>
                <div>
                  <label htmlFor="messaggio" className={LABEL}>{t.form.messaggio} {t.form.required}</label>
                  <textarea id="messaggio" data-testid="input-messaggio" className={`${FIELD} resize-none`} rows={4} required minLength={10} maxLength={5000} placeholder={t.form.messaggioPh} value={form.messaggio} onChange={update("messaggio")} />
                </div>
              </div>
              {status === "error" && (
                <p data-testid="contact-error" className="mt-5 rounded-lg bg-[#B89762]/10 px-4 py-3 text-sm text-[#E8C89A]">
                  {errorMsg}
                </p>
              )}
              <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={status === "sending"}
                className="mt-9 w-full rounded-full bg-[#B89762] py-4 text-sm font-medium text-[#1E2A38] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#C9A873] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "sending" ? t.form.sending : t.form.submit}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;